import SectionHeading from "./SectionHeading";
import { overviewInfo, overviewStats } from "@/mocks/movieContent";

export default function OverviewSection() {
  return (
    <section id="overview" className="scroll-mt-20 bg-background-50 py-16 md:py-24">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8">
        <SectionHeading
          eyebrow="影片概览"
          title="先花两分钟，了解这是一部什么电影"
          description="在进入具体的剧情与影评之前，先用一组基础信息帮你建立整体印象：它属于什么题材、围绕什么关系展开、又为什么会被反复讨论。"
          icon="ri-information-line"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              这部影片通常被归入都市情感与伦理剧情的方向。它并不依赖强烈的悬念或反转来吸引观众，而是把镜头对准一段
              <strong className="font-semibold text-foreground-950">由日常相处发展而来的关系</strong>
              ：主人公通过姐姐的社交圈认识了姐姐的朋友，两人在反复的接触中逐渐熟悉，情感也在不知不觉间发生变化。
            </p>
            <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              影片的讨论度大多集中在
              <strong className="font-semibold text-foreground-950">“关系”与“边界”</strong>
              上。它把更多笔墨放在人物的犹豫、克制与选择上，因此不同观众往往会得出不一样的理解。这也是为什么关于它的影评、解说说法很多，
              与其只看结论，不如自己看完再判断。
            </p>
            <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              需要说明的是，本文所有内容都属于一般性介绍与观点整理，涉及题材与内容的提示仅供参考；观看请通过正规、合法的渠道，
              并根据自身情况理性选择。
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {overviewStats.map((stat) => (
                <div key={stat.unit} className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                  <p className="font-heading text-3xl font-bold text-primary-600">
                    {stat.value}
                    <span className="ml-1 text-sm font-medium text-foreground-600">{stat.unit}</span>
                  </p>
                  <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed">{stat.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="col-span-2 w-full h-56 md:h-72 rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=rain%20streaked%20apartment%20window%20at%20dusk%20overlooking%20a%20softly%20glowing%20city%2C%20warm%20amber%20streetlights%2C%20blurred%20bokeh%2C%20melancholic%20nostalgic%20mood%2C%20cinematic%20warm%20color%20grading%2C%20soft%20film%20grain%2C%20editorial%20photography%2C%20atmospheric%20and%20elegant&width=900&height=600&seq=hap-ov-01&orientation=landscape"
                alt="雨夜窗边与城市暖色灯光，呼应影片的都市情感氛围"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-full h-44 md:h-56 rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=vintage%20film%20reel%20and%20projector%20on%20a%20wooden%20table%20in%20a%20dim%20warm%20room%2C%20dust%20particles%20floating%20in%20a%20beam%20of%20golden%20light%2C%20nostalgic%20cinematic%20still%20life%2C%20deep%20shadows%2C%20film%20grain%2C%20editorial%20photography%2C%20warm%20amber%20palette&width=600&height=800&seq=hap-ov-02&orientation=portrait"
                alt="老式胶片与放映机，象征电影的影像质感"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-full h-44 md:h-56 rounded-lg overflow-hidden border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20late%20night%20kitchen%20with%20two%20cups%20of%20tea%20on%20a%20table%2C%20warm%20lamp%20light%2C%20soft%20shadows%2C%20quiet%20intimate%20domestic%20atmosphere%2C%20cinematic%20warm%20color%20grading%2C%20film%20grain%2C%20editorial%20photography%2C%20elegant%20and%20atmospheric&width=600&height=800&seq=hap-ov-03&orientation=portrait"
                alt="深夜厨房里的两杯热茶，体现日常生活的亲密感"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-14 rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-500 text-background-50">
              <i className="ri-list-check-2 text-base"></i>
            </span>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">基础信息一览</h3>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
            {overviewInfo.map((item) => (
              <div key={item.label} className="border-t border-background-300/60 pt-3">
                <dt className="text-xs text-foreground-500 font-label tracking-wide">{item.label}</dt>
                <dd className="mt-1.5 text-sm md:text-base text-foreground-900 font-medium leading-snug">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}